import { type DesiredEvent, parseShiftMarker } from "./shift-events";

/**
 * Wat er van ons in de agenda staat. Deze lijst bevat uitsluitend afspraken die onze handtekening
 * dragen — zie `parseShiftMarker`. Alles daarbuiten is van de gebruiker en raken we nooit aan.
 */
export interface KnownEvent {
  shiftId: string;
  eventId: string;
  title: string;
  location: string | null;
  description: string;
  startDate: number;
  endDate: number;
}

export interface CalendarPlan {
  create: DesiredEvent[];
  update: { eventId: string; event: DesiredEvent }[];
  /** Event-id's, geen dienst-id's: dit gaat rechtstreeks naar `deleteEvent`. */
  remove: string[];
}

export interface ReconcileWindow {
  from: number;
  to: number;
}

/** Wat we van een afspraak op het toestel nodig hebben; `CalendarEvent` past hier structureel in. */
export interface DeviceEvent {
  id: string;
  title: string;
  calendarId: string | null;
  location: string | null;
  description: string | null;
  startDate: number;
  endDate: number;
}

/**
 * Filtert de afspraken op het toestel terug tot die van ons.
 *
 * Dit is de enige plek waar iets "van ons" wordt genoemd, en daarmee de enige poort naar
 * `reconcile`'s verwijderlijst. Twee eisen, allebei nodig: in de gekoppelde agenda, én met onze
 * handtekening in de beschrijving. Zonder de tweede zou "koppeling verbreken" de hele agenda
 * leegruimen; zonder de eerste zouden we afspraken aanraken in een agenda die de medewerker
 * inmiddels niet meer gekozen heeft.
 *
 * Meteen ook het herstelpad: raakt de opslag kwijt (herinstallatie), dan vinden we onze afspraken
 * hier gewoon terug in plaats van er een tweede set naast te zetten.
 */
export function toKnownEvents(events: DeviceEvent[], calendarId: string): KnownEvent[] {
  const known: KnownEvent[] = [];

  for (const event of events) {
    if (event.calendarId !== calendarId) {
      continue;
    }

    const shiftId = parseShiftMarker(event.description);
    if (!shiftId) {
      continue;
    }

    known.push({
      shiftId,
      eventId: event.id,
      title: event.title,
      location: event.location,
      description: event.description ?? "",
      startDate: event.startDate,
      endDate: event.endDate,
    });
  }

  return known;
}

/**
 * Wat we schrijven komt er niet altijd letterlijk hetzelfde weer uit: Android geeft een leeg veld
 * terug als `""` waar wij `null` stuurden, en regeleindes kunnen als CRLF terugkomen. Zonder deze
 * normalisatie ziet elke synchronisatie een "wijziging" en werken we elke vijf minuten de hele
 * agenda opnieuw bij.
 */
function normalize(value: string | null): string {
  return (value ?? "").replace(/\r\n/g, "\n").trim();
}

function differs(known: KnownEvent, desired: DesiredEvent): boolean {
  return (
    normalize(known.title) !== normalize(desired.title) ||
    normalize(known.location) !== normalize(desired.location) ||
    normalize(known.description) !== normalize(desired.description) ||
    known.startDate !== desired.startDate ||
    known.endDate !== desired.endDate
  );
}

/**
 * Bepaalt wat er moet gebeuren om de agenda gelijk te trekken met de diensten.
 *
 * Twee dingen zitten er bewust in:
 *
 * - **Wijzigen, niet opnieuw aanmaken.** Een verzette dienst hoort dezelfde afspraak te houden;
 *   anders staat de agenda binnen een week vol duplicaten. (De ICS-kant lost datzelfde op met een
 *   stabiele `UID`.)
 * - **Buiten het venster blijft alles staan.** Het venster schuift mee met vandaag, dus een gewerkte
 *   dienst van vorige maand valt er vanzelf uit. Die dan weggooien zou iemands werkverleden uit
 *   zijn agenda wissen.
 *
 * Verhuist een dienst juist búiten het venster, dan verdwijnt hij hier (hij zit niet meer in
 * `desired`) en komt hij terug zodra het venster hem weer bereikt. Dat is de prijs van een venster,
 * en zichtbaar beter dan een afspraak op de verkeerde datum.
 */
export function reconcile(options: {
  desired: DesiredEvent[];
  known: KnownEvent[];
  window: ReconcileWindow;
}): CalendarPlan {
  const { desired, known, window } = options;

  const inWindow = (start: number) => start >= window.from && start <= window.to;

  // Ontdubbelen op dienst, niet op volgorde vertrouwen: zou de lijst een dienst twee keer bevatten
  // (een overlappende pagina bijvoorbeeld), dan maken we er anders twee afspraken van.
  const desiredInWindow = [
    ...new Map(
      desired.filter((event) => inWindow(event.startDate)).map((event) => [event.shiftId, event]),
    ).values(),
  ];
  const knownByShift = new Map(known.map((event) => [event.shiftId, event]));
  const desiredIds = new Set(desiredInWindow.map((event) => event.shiftId));

  const plan: CalendarPlan = { create: [], update: [], remove: [] };

  for (const event of desiredInWindow) {
    const existing = knownByShift.get(event.shiftId);
    if (!existing) {
      plan.create.push(event);
    } else if (differs(existing, event)) {
      plan.update.push({ eventId: existing.eventId, event });
    }
  }

  for (const event of known) {
    if (inWindow(event.startDate) && !desiredIds.has(event.shiftId)) {
      plan.remove.push(event.eventId);
    }
  }

  return plan;
}
